// components/customer/primary-picker/CustomerPrimaryPickers.tsx
import { useState } from 'react';
import { ActionIcon, Box, SimpleGrid } from '@mantine/core';
import { IconBrandWhatsapp, IconMailbox, IconTruckDelivery, IconUser } from '@tabler/icons-react';
import * as Enums from '@/utils/enums';
import Fetch from '@/utils/Fetch';
import PS from '@/services/permission/permission-service';
import ManageContact from '@/components/modals/contact/manage-contact';
import LocationForm from '@/PageComponents/customer/LocationForm';
import { DETAILS_GRID_PROPS } from '@/components/customer/details-layout';
import PrimaryPickerField from './PrimaryPickerField';
import classes from './PrimaryPickerField.module.css';
import {
  activeContactsOf, activeLocationsOf, contactName, locationName, mergeSavedContact, mergeSavedLocation,
  primaryContactOf, primaryLocationOf, setPrimaryContact, setPrimaryLocation,
  whatsAppUrl, type PickerContact, type PickerCustomer, type PickerLocation,
} from './primaryUpdates';

export interface CustomerPrimaryPickersProps {
  customer: PickerCustomer;
  updateCustomer: (next: PickerCustomer, setDirty?: boolean) => void;
  accessStatus?: number;
}

type ModalState =
  | { kind: 'contact'; contact: PickerContact | null }
  | { kind: 'location'; location: PickerLocation | null; locationType: number }
  | null;

const LOCATION_FIELDS = [
  { type: Enums.LocationType.Delivery, label: 'Primary Delivery Address', noun: 'delivery address',
    icon: <IconTruckDelivery size={20} />, tag: 'delivery' as const, testId: 'primary-picker-delivery' },
  { type: Enums.LocationType.Postal, label: 'Primary Postal Address', noun: 'postal address',
    icon: <IconMailbox size={20} />, tag: 'postal' as const, testId: 'primary-picker-postal' },
];

export default function CustomerPrimaryPickers({ customer, updateCustomer, accessStatus }: CustomerPrimaryPickersProps) {
  const [editPermission] = useState(() => PS.hasPermission(Enums.PermissionName.EditCustomer));
  const locked = accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess;
  const canEdit = editPermission && !locked;

  const [modal, setModal] = useState<ModalState>(null);

  const contacts = activeContactsOf(customer);
  const primaryContact = primaryContactOf(customer);

  // Picking a row makes it primary straight away; the change is saved with the page's Save.
  const pickContact = (contact: PickerContact) =>
    updateCustomer(setPrimaryContact(customer, contact), true);

  const pickLocation = (location: PickerLocation) =>
    updateCustomer(setPrimaryLocation(customer, location), true);

  // ManageContact/LocationForm already saved to the API. A new item marks the form dirty
  // (as before); an edit only refreshes local state.
  // ManageContact also passes the other active contacts as reloaded after the save: moving a role
  // changes them server side, and keeping their old RowVersion would fail their next save.
  // The merge keeps a primary picked here but not yet saved (see mergeSavedContact).
  const onContactSaved = async (contact: PickerContact, isNew: boolean, related: PickerContact[] = []) => {
    const next = await withServerCustomerVersion(mergeSavedContact(customer, contact, related));
    setModal(null);
    updateCustomer(next, isNew);
  };

  // related: the other active locations as reloaded after the save (see onContactSaved)
  const onLocationSaved = async (location: PickerLocation, isNew: boolean, related: PickerLocation[] = []) => {
    const next = await withServerCustomerVersion(mergeSavedLocation(customer, location, related));
    setModal(null);
    updateCustomer(next, isNew);
  };

  // The API re-saves the customer after every contact or location save (to refresh its search cache),
  // which gives the customer a new RowVersion. Take it, or the page's next Save fails the concurrency check.
  // Only the version fields are taken, so unsaved edits on the page are kept.
  const withServerCustomerVersion = async (next: PickerCustomer): Promise<PickerCustomer> => {
    try {
      const stored = await Fetch.get({ url: `/Customer/${customer.ID}` } as any);
      if (stored?.ID && stored.RowVersion) {
        return { ...next, RowVersion: stored.RowVersion, ModifiedBy: stored.ModifiedBy, ModifiedDate: stored.ModifiedDate };
      }
    } catch (e) {
      // Keep the page's version; Save reports the conflict
    }
    return next;
  };

  const whatsAppButton = (contact: PickerContact) =>
    contact.MobileNumber ? (
      <ActionIcon
        radius="xl"
        size={26}
        className={classes.whatsapp}
        name="wa-customer-contact-chat"
        aria-label="Chat via WhatsApp"
        title="Chat via WhatsApp"
        onKeyDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          window.open(whatsAppUrl(contact.MobileNumber!), '_blank');
        }}
      >
        <IconBrandWhatsapp size={16} />
      </ActionIcon>
    ) : null;

  return (
    <Box>
      <SimpleGrid {...DETAILS_GRID_PROPS}>
        <PrimaryPickerField<PickerContact>
          label="Primary Contact"
          noun="contact"
          icon={<IconUser size={20} />}
          items={contacts}
          current={primaryContact}
          getId={c => c.ID}
          getName={contactName}
          getValueLines={c => ({ main: contactName(c), sub: [c.EmailAddress ?? '', c.MobileNumber ?? ''] })}
          getOptionSubLines={c => [c.EmailAddress ?? '', c.MobileNumber ?? '']}
          getTags={c => (c.IsPrimaryAccount ? ['Accounting'] : [])}
          getSearchText={c => [contactName(c), c.EmailAddress, c.MobileNumber].filter(Boolean).join(' ')}
          searchPlaceholder="Search contacts"
          tagVariant="delivery"
          canEdit={canEdit}
          valueActions={whatsAppButton}
          onPick={pickContact}
          onEdit={c => setModal({ kind: 'contact', contact: c })}
          onAdd={() => setModal({ kind: 'contact', contact: null })}
          testId="primary-picker-contact"
        />
      </SimpleGrid>

      <SimpleGrid {...DETAILS_GRID_PROPS}>
        {LOCATION_FIELDS.map(f => (
          <PrimaryPickerField<PickerLocation>
            key={f.type}
            label={f.label}
            noun={f.noun}
            icon={f.icon}
            items={activeLocationsOf(customer, f.type)}
            current={primaryLocationOf(customer, f.type)}
            getId={l => l.ID}
            getName={locationName}
            getValueLines={l => ({ main: l.LocationDisplay || locationName(l), sub: [l.Description ?? ''] })}
            getOptionSubLines={l => [l.LocationDisplay ?? '']}
            getSearchText={l => [l.Description, l.LocationDisplay].filter(Boolean).join(' ')}
            searchPlaceholder="Search addresses"
            tagVariant={f.tag}
            canEdit={canEdit}
            onPick={pickLocation}
            onEdit={l => setModal({ kind: 'location', location: l, locationType: f.type })}
            onAdd={() => setModal({ kind: 'location', location: null, locationType: f.type })}
            testId={f.testId}
          />
        ))}
      </SimpleGrid>

      {modal?.kind === 'contact' && (
        <ManageContact
          isNew={!modal.contact}
          contact={modal.contact}
          module={Enums.Module.Customer}
          moduleData={customer}
          accessStatus={accessStatus}
          onSave={(c: PickerContact, related?: PickerContact[]) => onContactSaved(c, !modal.contact, related)}
          onCancel={() => setModal(null)}
        />
      )}

      {modal?.kind === 'location' && (
        <LocationForm
          isNew={!modal.location}
          location={(modal.location ?? { LocationType: modal.locationType }) as any}
          module={Enums.Module.Customer}
          moduleData={customer}
          accessStatus={accessStatus}
          onSave={(l: PickerLocation, related?: PickerLocation[]) => onLocationSaved(l, !modal.location, related)}
          onCancel={() => setModal(null)}
          defaultValues={modal.location ? undefined : {
            primaryToggle: {
              // Same rule as location-selector: the first location of a type is always primary.
              alwaysChecked: (t: any) => activeLocationsOf(customer, (Enums.LocationType as any)[t]).length === 0,
              color: (t: any) => activeLocationsOf(customer, (Enums.LocationType as any)[t]).length === 0
                ? 'var(--mantine-color-blue-1)' : undefined,
            },
          }}
        />
      )}
    </Box>
  );
}
